import type {
  FirstGenerationEntryPhase,
  FirstGenerationJourneyState,
  PlanStatus,
  StyleAnalysisStatus,
  StyleImageNaturalSize,
} from './types';
import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';
import type { NormalizedImageRegion, StyleInsightKey, StyleInsightResult } from '@/services/project/referenceStyleAnalysis';
import { create } from 'zustand';

interface FirstGenerationJourneyActions {
  begin: (
    projectId: string,
    selectedReference: ReferenceImage,
    context?: {
      runId?: string;
      messageId?: string;
      baseVersionId?: string;
      entryPhase?: FirstGenerationEntryPhase;
    },
  ) => void;
  bindGenerationMessage: (projectId: string, runId: string, messageId: string) => void;
  bindGenerationResult: (
    projectId: string,
    messageId: string,
    versionId: string,
    allowReplacement?: boolean,
  ) => void;
  restoreCreate: (
    projectId: string,
    selectedReference?: ReferenceImage,
    context?: {
      runId?: string;
      messageId?: string;
      baseVersionId?: string;
    },
  ) => void;
  suspend: (projectId: string) => void;
  resume: (
    projectId: string,
    context?: { runId?: string; messageId?: string },
  ) => void;
  advanceToStyle: (projectId: string) => void;
  startCreate: (projectId: string) => void;
  forceStartCreate: (projectId: string) => void;
  completeStylePresentation: (projectId: string) => void;
  markGenerationCompleted: (projectId: string, messageId?: string) => void;
  startDeliveryCheck: (projectId: string, versionId: string) => void;
  setDeliveryCheckVersion: (projectId: string, versionId: string) => void;
  passDeliveryCheck: (projectId: string) => void;
  retryDeliveryCheck: (projectId: string, error: string) => void;
  failDeliveryCheck: (projectId: string, error: string) => void;
  failInitialDelivery: (projectId: string, error: string) => void;
  startDeliveryRecovery: (projectId: string) => void;
  completeDeliveryRecovery: (projectId: string) => void;
  failDeliveryRecovery: (projectId: string, error?: string) => void;
  clearDeliveryRecovery: (projectId: string) => void;
  beginDelivery: (projectId: string) => void;
  showDeliveryShowcase: (projectId: string) => void;
  startDeliveryExpansion: (projectId: string) => void;
  markDeliveryReady: (projectId: string) => void;
  exitDelivery: (projectId: string) => void;
  startDeliveryBackground: (projectId: string) => void;
  setDeliveryBackground: (projectId: string, imageUrl: string) => void;
  failDeliveryBackground: (projectId: string, reason?: string) => void;
  setPlanStatus: (status: PlanStatus) => void;
  setStyleAnalysisStatus: (status: StyleAnalysisStatus) => void;
  setStyleImageNaturalSize: (size: StyleImageNaturalSize) => void;
  failStyleAnalysis: () => void;
  resetIncompleteStyleAnalysis: () => void;
  setAnalysisFocus: (key: StyleInsightKey, region: NormalizedImageRegion) => void;
  setAnalysisPalette: (swatches: string[]) => void;
  appendAnalysisDelta: (key: StyleInsightKey, field: 'headline' | 'description', delta: string) => void;
  setInsight: (insight: StyleInsightResult) => void;
  setStyleThought: (sequence: 1 | 2, text: string) => void;
  setCurrentInsightKey: (key?: StyleInsightKey) => void;
  finish: (projectId?: string) => void;
  reset: () => void;
}

let journeyAttemptSequence = 0;

function createJourneyAttemptId(projectId: string): string {
  journeyAttemptSequence += 1;
  return `${projectId}:${Date.now().toString(36)}:${journeyAttemptSequence.toString(36)}`;
}

const INITIAL_STATE: FirstGenerationJourneyState = {
  projectId: undefined,
  journeyAttemptId: undefined,
  entryType: 'confirmed_reference',
  phase: 'idle',
  planSkipped: false,
  executionStatus: 'idle',
  selectedReference: undefined,
  generationRunId: undefined,
  generationMessageId: undefined,
  generationVersionId: undefined,
  generationBaseVersionId: undefined,
  createSignalReceived: false,
  stylePresentationCompleted: false,
  generationCompleted: false,
  generationBaseVersionCaptured: false,
  completedGenerationRunId: undefined,
  generationCompletedAt: undefined,
  createStartedAt: undefined,
  deliveryCheckStatus: 'idle',
  deliveryCheckStartedAt: undefined,
  deliveryCheckError: undefined,
  deliveryVersionId: undefined,
  deliveryStage: 'idle',
  deliveryReturnedAt: undefined,
  deliveryBackgroundStatus: 'idle',
  deliveryBackgroundFailureReason: undefined,
  deliveryBackgroundUrl: undefined,
  planStatus: 'idle',
  styleAnalysisStatus: 'idle',
  styleImageNaturalSize: undefined,
  currentInsightKey: undefined,
  analysisOrder: [],
  focusRegions: {},
  insightSwatches: {},
  insightDrafts: {},
  insights: {},
  styleThoughts: [],
};

function canStartCreate(state: FirstGenerationJourneyState, projectId: string): boolean {
  return state.projectId === projectId
    && state.phase !== 'idle'
    && state.phase !== 'create'
    && state.phase !== 'checking'
    && state.phase !== 'delivery'
    && state.phase !== 'finished';
}

function buildCreateState(state: FirstGenerationJourneyState): Partial<FirstGenerationJourneyState> {
  return {
    phase: 'create',
    createSignalReceived: true,
    createStartedAt: Date.now(),
    planStatus: 'completed',
    styleAnalysisStatus: state.styleAnalysisStatus === 'completed' ? 'completed' : 'aborted',
    currentInsightKey: undefined,
  };
}

export const useFirstGenerationJourneyStore = create<FirstGenerationJourneyState & FirstGenerationJourneyActions>(set => ({
  ...INITIAL_STATE,
  begin: (projectId, selectedReference, context) => {
    const entryPhase = context?.entryPhase ?? 'plan';
    return set({
      ...INITIAL_STATE,
      projectId,
      journeyAttemptId: createJourneyAttemptId(projectId),
      entryType: 'confirmed_reference',
      selectedReference,
      generationRunId: context?.runId,
      generationMessageId: context?.messageId,
      generationBaseVersionId: context?.baseVersionId,
      generationBaseVersionCaptured: Boolean(
        context && Object.prototype.hasOwnProperty.call(context, 'baseVersionId'),
      ),
      phase: entryPhase,
      planSkipped: entryPhase === 'style',
      planStatus: entryPhase === 'style' ? 'completed' : 'idle',
      executionStatus: 'active',
    });
  },
  bindGenerationMessage: (projectId, runId, messageId) => set((state) => {
    if (
      state.projectId !== projectId
      || state.generationRunId !== runId
      || !messageId
      || state.generationMessageId === messageId
    ) {
      return state;
    }
    return { generationMessageId: messageId };
  }),
  bindGenerationResult: (
    projectId,
    messageId,
    versionId,
    allowReplacement = false,
  ) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase === 'idle'
      || state.phase === 'finished'
      || !messageId
      || !versionId
      || versionId === state.generationBaseVersionId
    ) {
      return state;
    }
    const isReplacingBoundResult = Boolean(
      (state.generationMessageId && state.generationMessageId !== messageId)
      || (state.generationVersionId && state.generationVersionId !== versionId),
    );
    if (isReplacingBoundResult && !allowReplacement) {
      return state;
    }
    if (
      state.generationMessageId === messageId
      && state.generationVersionId === versionId
    ) {
      return state;
    }
    return {
      generationMessageId: messageId,
      generationVersionId: versionId,
    };
  }),
  restoreCreate: (projectId, selectedReference, context) => set({
    ...INITIAL_STATE,
    projectId,
    journeyAttemptId: createJourneyAttemptId(projectId),
    entryType: 'recovery',
    selectedReference,
    generationRunId: context?.runId,
    generationMessageId: context?.messageId,
    generationBaseVersionId: context?.baseVersionId,
    generationBaseVersionCaptured: Boolean(
      context && Object.prototype.hasOwnProperty.call(context, 'baseVersionId'),
    ),
    phase: 'create',
    executionStatus: 'active',
    createSignalReceived: true,
    createStartedAt: Date.now(),
    planStatus: 'completed',
    styleAnalysisStatus: 'aborted',
  }),
  suspend: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.executionStatus !== 'active'
      || (state.phase !== 'plan' && state.phase !== 'style' && state.phase !== 'create')
    ) {
      return state;
    }
    return { executionStatus: 'suspended' };
  }),
  resume: (projectId, context) => set((state) => {
    const hasRunId = Boolean(context?.runId);
    const hasMessageId = Boolean(context?.messageId);
    const canRebindActiveRun = state.executionStatus === 'active'
      && hasRunId
      && hasMessageId
      && (
        state.generationRunId !== context?.runId
        || state.generationMessageId !== context?.messageId
      );
    if (
      state.projectId !== projectId
      || (state.executionStatus !== 'suspended' && !canRebindActiveRun)
      || (state.phase !== 'plan' && state.phase !== 'style' && state.phase !== 'create')
    ) {
      return state;
    }
    if (hasRunId !== hasMessageId) {
      return state;
    }
    if (!hasRunId || !hasMessageId) {
      return { executionStatus: 'active' };
    }
    return {
      executionStatus: 'active',
      generationRunId: context.runId,
      generationMessageId: context.messageId,
      generationVersionId: undefined,
      completedGenerationRunId: undefined,
      generationCompleted: false,
      generationCompletedAt: undefined,
      deliveryCheckStatus: 'idle',
      deliveryCheckStartedAt: undefined,
      deliveryCheckError: undefined,
      deliveryVersionId: undefined,
    };
  }),
  advanceToStyle: projectId => set((state) => {
    if (state.projectId !== projectId || state.phase !== 'plan') {
      return state;
    }
    return { phase: 'style', planStatus: 'completed' };
  }),
  startCreate: projectId => set((state) => {
    if (!canStartCreate(state, projectId)) {
      return state;
    }
    if (state.phase === 'plan' || (state.phase === 'style' && !state.stylePresentationCompleted)) {
      return { createSignalReceived: true };
    }
    return buildCreateState(state);
  }),
  forceStartCreate: projectId => set((state) => {
    if (!canStartCreate(state, projectId)) {
      return state;
    }
    return buildCreateState(state);
  }),
  completeStylePresentation: projectId => set((state) => {
    if (state.projectId !== projectId || state.phase !== 'style' || state.stylePresentationCompleted) {
      return state;
    }
    if (!state.createSignalReceived) {
      return { stylePresentationCompleted: true };
    }
    return {
      phase: 'create',
      stylePresentationCompleted: true,
      createStartedAt: Date.now(),
      planStatus: 'completed',
      currentInsightKey: undefined,
    };
  }),
  markGenerationCompleted: (projectId, messageId) => set((state) => {
    if (state.projectId !== projectId || state.phase === 'idle' || state.phase === 'finished') {
      return state;
    }
    if (state.generationRunId && !state.generationMessageId) {
      return state;
    }
    if (
      state.generationMessageId
      && (!messageId || state.generationMessageId !== messageId)
    ) {
      return state;
    }
    const completion = {
      executionStatus: 'active' as const,
      generationCompleted: true,
      completedGenerationRunId: state.generationRunId,
      generationCompletedAt: Date.now(),
    };
    if (state.phase === 'plan') {
      return { ...completion, createSignalReceived: true };
    }
    if (state.phase === 'style' && state.stylePresentationCompleted) {
      return {
        phase: 'create',
        ...completion,
        createSignalReceived: true,
        createStartedAt: Date.now(),
        planStatus: 'completed',
        styleAnalysisStatus: state.styleAnalysisStatus === 'completed' ? 'completed' : 'aborted',
        currentInsightKey: undefined,
      };
    }
    if (state.phase === 'style') {
      return {
        ...completion,
        createSignalReceived: true,
      };
    }
    return completion;
  }),
  startDeliveryCheck: (projectId, versionId) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'create'
      || !state.generationCompleted
      || !versionId
      || !state.generationMessageId
      || state.generationVersionId !== versionId
      || (
        state.generationRunId
        && state.completedGenerationRunId !== state.generationRunId
      )
    ) {
      return state;
    }
    return {
      phase: 'checking',
      deliveryCheckStatus: 'checking',
      deliveryCheckStartedAt: Date.now(),
      deliveryCheckError: undefined,
      deliveryVersionId: versionId,
      currentInsightKey: undefined,
    };
  }),
  setDeliveryCheckVersion: (projectId, versionId) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'checking'
      || state.deliveryCheckStatus !== 'checking'
      || !versionId
    ) {
      return state;
    }
    if (state.deliveryVersionId === versionId) {
      return state;
    }
    return {
      deliveryVersionId: versionId,
      deliveryCheckError: undefined,
    };
  }),
  passDeliveryCheck: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'checking'
      || state.deliveryCheckStatus !== 'checking'
      || !state.deliveryVersionId
    ) {
      return state;
    }
    return { deliveryCheckStatus: 'ready', deliveryCheckError: undefined };
  }),
  retryDeliveryCheck: (projectId, error) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'checking'
      || state.deliveryCheckStatus !== 'checking'
    ) {
      return state;
    }
    return {
      deliveryCheckStartedAt: Date.now(),
      deliveryCheckError: error,
    };
  }),
  failDeliveryCheck: (projectId, error) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'checking'
      || state.deliveryCheckStatus !== 'checking'
    ) {
      return state;
    }
    return {
      phase: 'finished',
      executionStatus: 'idle',
      deliveryStage: 'idle',
      deliveryCheckStatus: 'recovering',
      deliveryCheckError: error,
      currentInsightKey: undefined,
    };
  }),
  failInitialDelivery: (projectId, error) => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase === 'idle'
      || state.phase === 'delivery'
      || state.phase === 'finished'
    ) {
      return state;
    }
    return {
      phase: 'finished',
      executionStatus: 'idle',
      deliveryStage: 'idle',
      deliveryCheckStatus: 'failed',
      deliveryCheckError: error,
      currentInsightKey: undefined,
      styleAnalysisStatus: state.styleAnalysisStatus === 'streaming' ? 'aborted' : state.styleAnalysisStatus,
    };
  }),
  startDeliveryRecovery: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || (
        state.deliveryCheckStatus !== 'failed'
        && state.deliveryCheckStatus !== 'recovering'
      )
    ) {
      return state;
    }
    return { deliveryCheckStatus: 'recovering' };
  }),
  completeDeliveryRecovery: projectId => set((state) => {
    if (state.projectId !== projectId || state.deliveryCheckStatus !== 'recovering') {
      return state;
    }
    return { deliveryCheckStatus: 'recovered', deliveryCheckError: undefined };
  }),
  failDeliveryRecovery: (projectId, error) => set((state) => {
    if (state.projectId !== projectId || state.deliveryCheckStatus !== 'recovering') {
      return state;
    }
    return {
      deliveryCheckStatus: 'failed',
      deliveryCheckError: error || state.deliveryCheckError,
    };
  }),
  clearDeliveryRecovery: projectId => set((state) => {
    if (state.projectId !== projectId || state.deliveryCheckStatus === 'checking') {
      return state;
    }
    return {
      deliveryCheckStatus: 'idle',
      deliveryCheckError: undefined,
    };
  }),
  beginDelivery: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'checking'
      || !state.generationCompleted
      || state.deliveryCheckStatus !== 'ready'
    ) {
      return state;
    }
    return {
      phase: 'delivery',
      deliveryStage: 'preparing',
      deliveryReturnedAt: undefined,
      currentInsightKey: undefined,
    };
  }),
  showDeliveryShowcase: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'delivery'
      || state.deliveryStage !== 'preparing'
    ) {
      return state;
    }
    return { deliveryStage: 'showcase' };
  }),
  startDeliveryExpansion: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'delivery'
      || state.deliveryStage !== 'showcase'
    ) {
      return state;
    }
    return { deliveryStage: 'expanding' };
  }),
  markDeliveryReady: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'delivery'
      || state.deliveryStage !== 'expanding'
    ) {
      return state;
    }
    return { deliveryStage: 'ready' };
  }),
  exitDelivery: projectId => set((state) => {
    if (
      state.projectId !== projectId
      || state.phase !== 'delivery'
      || state.deliveryStage === 'exiting'
    ) {
      return state;
    }
    return { deliveryStage: 'exiting' };
  }),
  startDeliveryBackground: projectId => set((state) => {
    if (state.projectId !== projectId || state.deliveryBackgroundStatus !== 'idle') {
      return state;
    }
    return {
      deliveryBackgroundStatus: 'loading',
      deliveryBackgroundFailureReason: undefined,
    };
  }),
  setDeliveryBackground: (projectId, imageUrl) => set((state) => {
    if (
      state.projectId !== projectId
      || state.deliveryBackgroundStatus !== 'loading'
      || !imageUrl
    ) {
      return state;
    }
    return {
      deliveryBackgroundStatus: 'ready',
      deliveryBackgroundFailureReason: undefined,
      deliveryBackgroundUrl: imageUrl,
    };
  }),
  failDeliveryBackground: (projectId, reason = 'background_unavailable') => set((state) => {
    if (state.projectId !== projectId || state.deliveryBackgroundStatus === 'ready') {
      return state;
    }
    return {
      deliveryBackgroundStatus: 'failed',
      deliveryBackgroundFailureReason: reason,
    };
  }),
  setPlanStatus: planStatus => set({ planStatus }),
  setStyleAnalysisStatus: styleAnalysisStatus => set({ styleAnalysisStatus }),
  setStyleImageNaturalSize: styleImageNaturalSize => set((state) => {
    if (
      state.styleImageNaturalSize?.width === styleImageNaturalSize.width
      && state.styleImageNaturalSize.height === styleImageNaturalSize.height
    ) {
      return state;
    }
    return { styleImageNaturalSize };
  }),
  failStyleAnalysis: () => set({ styleAnalysisStatus: 'failed' }),
  resetIncompleteStyleAnalysis: () => set((state) => {
    const completedKeys = new Set(Object.keys(state.insights) as StyleInsightKey[]);
    const focusRegions = Object.fromEntries(
      Object.entries(state.focusRegions).filter(([key]) => completedKeys.has(key as StyleInsightKey)),
    );
    const insightSwatches = Object.fromEntries(
      Object.entries(state.insightSwatches).filter(([key]) => completedKeys.has(key as StyleInsightKey)),
    );
    const insightDrafts = Object.fromEntries(
      Object.entries(state.insightDrafts).filter(([key]) => completedKeys.has(key as StyleInsightKey)),
    );
    return {
      styleAnalysisStatus: 'streaming',
      analysisOrder: state.analysisOrder.filter(key => completedKeys.has(key)),
      focusRegions,
      insightSwatches,
      insightDrafts,
    };
  }),
  setAnalysisFocus: (key, region) => set((state) => ({
    analysisOrder: state.analysisOrder.includes(key) ? state.analysisOrder : [...state.analysisOrder, key],
    focusRegions: { ...state.focusRegions, [key]: region },
  })),
  setAnalysisPalette: swatches => set((state) => ({
    insightSwatches: { ...state.insightSwatches, color_palette: swatches },
  })),
  appendAnalysisDelta: (key, field, delta) => set((state) => {
    const current = state.insightDrafts[key] ?? { headline: '', description: '' };
    const maxLength = field === 'headline' ? 80 : 240;
    return {
      insightDrafts: {
        ...state.insightDrafts,
        [key]: { ...current, [field]: `${current[field]}${delta}`.slice(0, maxLength) },
      },
    };
  }),
  setInsight: insight => set((state) => {
    if (state.insights[insight.key]) {
      return state;
    }
    return {
      analysisOrder: state.analysisOrder.includes(insight.key)
        ? state.analysisOrder
        : [...state.analysisOrder, insight.key],
      focusRegions: { ...state.focusRegions, [insight.key]: insight.region },
      insightSwatches: insight.swatches
        ? { ...state.insightSwatches, [insight.key]: insight.swatches }
        : state.insightSwatches,
      insightDrafts: {
        ...state.insightDrafts,
        [insight.key]: { headline: insight.headline, description: insight.description },
      },
      insights: { ...state.insights, [insight.key]: insight },
    };
  }),
  setStyleThought: (sequence, text) => set((state) => {
    const nextThoughts = [...state.styleThoughts];
    nextThoughts[sequence - 1] = text;
    return { styleThoughts: nextThoughts };
  }),
  setCurrentInsightKey: currentInsightKey => set({ currentInsightKey }),
  finish: projectId => set((state) => {
    if (projectId && state.projectId !== projectId) {
      return state;
    }
    if (
      state.phase === 'finished'
      && state.executionStatus === 'idle'
      && state.deliveryStage === 'idle'
    ) {
      return state;
    }
    return {
      phase: 'finished',
      executionStatus: 'idle',
      deliveryStage: 'idle',
      deliveryReturnedAt: state.phase === 'delivery' ? Date.now() : state.deliveryReturnedAt,
      currentInsightKey: undefined,
      styleAnalysisStatus: state.styleAnalysisStatus === 'streaming' ? 'aborted' : state.styleAnalysisStatus,
    };
  }),
  reset: () => set({ ...INITIAL_STATE }),
}));

export function beginFirstGenerationProcess(
  projectId: string,
  selectedReference: ReferenceImage,
  context?: {
    runId?: string;
    messageId?: string;
    baseVersionId?: string;
    entryPhase?: FirstGenerationEntryPhase;
  },
): void {
  useFirstGenerationJourneyStore.getState().begin(projectId, selectedReference, context);
}

export function suspendFirstGenerationProcess(projectId: string): void {
  useFirstGenerationJourneyStore.getState().suspend(projectId);
}

export function resumeFirstGenerationProcess(
  projectId: string,
  context?: { runId?: string; messageId?: string },
): void {
  useFirstGenerationJourneyStore.getState().resume(projectId, context);
}

export function forceFirstGenerationCreateStarted(projectId: string): void {
  useFirstGenerationJourneyStore.getState().forceStartCreate(projectId);
}

export function markFirstGenerationCompleted(projectId: string, messageId?: string): void {
  useFirstGenerationJourneyStore.getState().markGenerationCompleted(projectId, messageId);
}

/**
 * 首次产物发布失败后，后续重试一旦收到真实完成事件，复用 Create 的恢复链路
 * 重新进入版本绑定和交付检查，避免成功重试被 finished 状态吞掉。
 */
export function recoverFailedFirstGenerationDelivery(
  projectId: string,
  messageId: string,
): boolean {
  const journey = useFirstGenerationJourneyStore.getState();
  if (
    journey.projectId !== projectId
    || journey.phase !== 'finished'
    || journey.deliveryCheckStatus !== 'failed'
    || journey.deliveryReturnedAt !== undefined
  ) {
    return false;
  }

  const selectedReference = journey.selectedReference;
  const generationBaseVersionId = journey.generationBaseVersionId;
  journey.restoreCreate(projectId, selectedReference, {
    baseVersionId: generationBaseVersionId,
  });
  useFirstGenerationJourneyStore.getState().markGenerationCompleted(projectId, messageId);
  return true;
}

export function showFirstGenerationDeliveryShowcase(projectId: string): void {
  useFirstGenerationJourneyStore.getState().showDeliveryShowcase(projectId);
}

export function startFirstGenerationDeliveryExpansion(projectId: string): void {
  useFirstGenerationJourneyStore.getState().startDeliveryExpansion(projectId);
}

export function markFirstGenerationDeliveryReady(projectId: string): void {
  useFirstGenerationJourneyStore.getState().markDeliveryReady(projectId);
}

export function exitFirstGenerationDelivery(projectId: string): void {
  useFirstGenerationJourneyStore.getState().exitDelivery(projectId);
}

export function advanceFirstGenerationToStyle(projectId: string): void {
  useFirstGenerationJourneyStore.getState().advanceToStyle(projectId);
}

export * from './types';
